import React, { useEffect, useState } from 'react';
import { Loader2, Building2, Plus, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function AdminClinicTab() {
  const [busy, setBusy] = useState(true);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ type: 'email', value: '', clinic_name: '', days: 90 });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get('/admin/clinic-grants'); setRules(r.data.items || []); }
    catch { toast.error('Failed to load clinic grants'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.value.trim()) { toast.error('Enter an email or domain'); return; }
    setCreating(true);
    try {
      const body = { type: form.type, value: form.value.trim(), clinic_name: form.clinic_name.trim() };
      if (form.type === 'domain') body.days = Number(form.days) || 90;
      const r = await api.post('/admin/clinic-grants', body);
      toast.success(`Grant added — ${r.data.applied} existing user(s) granted`);
      setForm({ type: 'email', value: '', clinic_name: '', days: 90 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add grant'); }
    finally { setCreating(false); }
  };

  const toggle = async (g) => {
    try {
      await api.patch(`/admin/clinic-grants/${g.id}`, { active: !g.active });
      setRules((p) => p.map((x) => (x.id === g.id ? { ...x, active: !x.active } : x)));
      toast.success(`${g.value} ${!g.active ? 'active' : 'stopped (existing grants kept)'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (g) => {
    setRules((p) => p.filter((x) => x.id !== g.id));
    try { await api.delete(`/admin/clinic-grants/${g.id}`); } catch {}
  };

  if (busy) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>;

  return (
    <div className="space-y-4" data-testid="clinics-tab">
      <div className="rounded-2xl bg-white p-5 card-soft">
        <h3 className="flex items-center gap-2 font-extrabold"><Plus className="h-4 w-4 text-[hsl(var(--coral))]" />Grant a clinic Pro access</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          <strong>Email list</strong> = lifetime Pro for named staff (kept forever). <strong>Domain</strong> = anyone with that email domain gets Pro for a limited window (default 90 days). Applies to existing users now and auto-applies when staff sign up.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-5">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Grant by</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="clinic-create-type" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
              <option value="email">Email (lifetime)</option>
              <option value="domain">Domain (window)</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-muted-foreground">{form.type === 'email' ? 'Staff email' : 'Email domain'}</label>
            <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === 'email' ? 'vet@rosecityvet.com' : 'rosecityvet.com'} data-testid="clinic-create-value" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Clinic name</label>
            <input value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} placeholder="Rose City Cat Clinic" data-testid="clinic-create-name" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
          {form.type === 'domain' && (
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Window (days)</label>
              <input type="number" min={1} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="clinic-create-days" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
          )}
        </div>
        <button onClick={create} disabled={creating} data-testid="clinic-create-btn" className="coral-btn mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-70">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add grant
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Clinic</th><th className="p-3">Grant by</th><th className="p-3">Email / Domain</th><th className="p-3">Access</th><th className="p-3">Granted</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody data-testid="clinic-grants-table">
            {rules.map((g) => (
              <tr key={g.id} className="border-t border-border">
                <td className="p-3 font-semibold">{g.clinic_name || '—'}</td>
                <td className="p-3 capitalize">{g.type}</td>
                <td className="p-3 font-mono text-xs">{g.value}</td>
                <td className="p-3">{g.type === 'email' ? 'Lifetime' : `Until ${(g.end_date || '').slice(0, 10)}`}</td>
                <td className="p-3 font-semibold">{g.granted_count ?? 0}</td>
                <td className="p-3">
                  <button onClick={() => toggle(g)} data-testid={`clinic-toggle-${g.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${g.active ? 'border-green-200 bg-green-50 text-green-700' : 'border-neutral-200 bg-neutral-100 text-neutral-500'}`}>
                    <Power className="h-3 w-3" />{g.active ? 'Active' : 'Stopped'}
                  </button>
                </td>
                <td className="p-3"><button onClick={() => remove(g)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground"><Building2 className="mx-auto mb-2 h-6 w-6 opacity-40" />No clinic grants yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
